import { useCallback, useEffect, useRef, MutableRefObject } from "react";

function createAudioElement(src: string): HTMLAudioElement {
  const audio = new Audio(src);
  audio.preload = "auto";
  return audio;
}

async function playAudioElement(
  audio: HTMLAudioElement | null,
  isPlayingRef: MutableRefObject<boolean>,
): Promise<void> {
  if (!audio || isPlayingRef.current) {
    return;
  }

  isPlayingRef.current = true;
  audio.currentTime = 0;

  const releaseLock = () => {
    isPlayingRef.current = false;
  };

  audio.onended = releaseLock;

  try {
    await audio.play();
  } catch {
    releaseLock();
  }
}

export function useOrderAlertSounds(
  newOrderSoundSrc: string,
  cancelSoundSrc: string,
  dealerId: string | undefined,
) {
  const newOrderAudioRef = useRef<HTMLAudioElement | null>(null);
  const cancelAudioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingNewOrderRef = useRef(false);
  const isPlayingCancelRef = useRef(false);

  useEffect(() => {
    if (!dealerId) {
      return undefined;
    }

    if (!newOrderAudioRef.current) {
      newOrderAudioRef.current = createAudioElement(newOrderSoundSrc);
    }

    if (!cancelAudioRef.current) {
      cancelAudioRef.current = createAudioElement(cancelSoundSrc);
    }

    return undefined;
  }, [cancelSoundSrc, dealerId, newOrderSoundSrc]);

  useEffect(() => {
    const unlockAudioPlayback = () => {
      [newOrderAudioRef.current, cancelAudioRef.current].forEach((audio) => {
        if (!audio) {
          return;
        }

        audio
          .play()
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
          })
          .catch(() => undefined);
      });
    };

    window.addEventListener("click", unlockAudioPlayback, { once: true });
    window.addEventListener("touchstart", unlockAudioPlayback, { once: true });
    window.addEventListener("keydown", unlockAudioPlayback, { once: true });

    return () => {
      window.removeEventListener("click", unlockAudioPlayback);
      window.removeEventListener("touchstart", unlockAudioPlayback);
      window.removeEventListener("keydown", unlockAudioPlayback);
    };
  }, []);

  const playNewOrderSound = useCallback(async () => {
    await playAudioElement(newOrderAudioRef.current, isPlayingNewOrderRef);
  }, []);

  const playCancelSound = useCallback(async () => {
    await playAudioElement(cancelAudioRef.current, isPlayingCancelRef);
  }, []);

  return { playNewOrderSound, playCancelSound };
}
